import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FieldType } from '@ngx-formly/material/form-field';
import {
  cloneDeep,
  defaultTo,
  get,
  isArray,
  isEmpty,
  isEqual,
  orderBy,
  sortBy,
  uniq,
  uniqBy,
} from 'lodash';
import { Observable, Subscription, fromEvent, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  take,
} from 'rxjs/operators';
import { SearchFilterService } from '../../../services/search-filter.service';
import { ContactSelfServiceFormsService } from '../../contact-self-service-forms/contact-self-service-forms.service';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'app-object-chooser-field',
  templateUrl: './object-chooser-field.component.html',
})
export class FormlyFieldObjectChooserFieldComponent
  extends FieldType
  implements OnInit, OnDestroy, AfterContentChecked
{
  selectedChange: EventEmitter<Array<string> | null> =
    new EventEmitter<Array<string> | null>();
  subscription = new Subscription();
  selectedValue = [];
  searchBar = new UntypedFormControl('');
  minimumSearchLength = 3;
  offSetValue = 0;
  showMoreHide = false;
  /* Below variable is to keep track of formControl value,
  if true then the object chooser value was fetched from server and populated */
  isValueLoaded: boolean = false;
  contactSelfServiceJWTToken: string; // JWT token for contact self service registration/update form

  @ViewChild(MatInput) fromFieldInput: MatInput;
  @ViewChild(MatSelect) matSelect: MatSelect;
  oldMatSelectKeydown: any;
  handleKeyDownSub: Subscription;

  previousSelectedValue = []; // holds the previous value of selectedValue
  originalObjectChooserValues = []; // back up of objectChooser options. This is used to maintain the original order of displayed objectChooser options.
  searchedObjectChooserOptions = []; // this holds the options displayed on performing search
  searchedSelectedObjectChooserValues = []; // this holds the values(tips) of options selected on the search dropdown
  backUpOfMultiSelectedValuesBeforeSearch = []; // this holds the values(tips) of selectedValue variable  before search
  /**
   * this holds the options of modified object chooser using displaySelectedItemsOnTop().
   * When we perform an empty search, we need to return the last modified object chooser options.
   * That is when this variable is used.
   *  */
  backUpOfMultiSelectedOptionsBeforeSearch = [];
  sortingDirection: string = 'asc';

  constructor(
    private searchService: SearchFilterService,
    private contactSelfService: ContactSelfServiceFormsService,
    protected route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.selectedValue = this.to.isMultiSelect ? [] : null;
    this.originalObjectChooserValues = cloneDeep(this.to.objectChooserOptions);
    this.sortingDirection = defaultTo(
      get(this, 'field.templateOptions.filter.sortingOptions.0.sortDirection'),
      'asc'
    );
    const pathName = this.getPathName();
    this.subscription.add(
      this.formControl.valueChanges.subscribe((value) => {
        if (!value) {
          this.selectedValue = this.to.isMultiSelect ? [] : null;
        } else {
          // if the value is received for the first time in the form control, call fetchObjectChooserValues()
          if (
            !this.isValueLoaded &&
            !isEmpty(value) &&
            pathName?.indexOf('contact-form/update') > -1
          ) {
            this.fetchObjectChooserValues(value);
          }
        }
      })
    );
    this.subscription.add(
      this.route.queryParams.subscribe((params) => {
        this.contactSelfServiceJWTToken = params['token'];
      })
    );
    this.searchDebounce();
  }

  onOpen(): void {
    this.handleKeyDownSub = fromEvent(
      (this.fromFieldInput as any)._elementRef.nativeElement,
      'keydown'
    ).subscribe((e: KeyboardEvent) => {
      this.handleInputKeyDown(e);
    });

    this.fromFieldInput.focus();
    this.matSelectHandleKeyDown();
  }

  handleInputKeyDown(e: KeyboardEvent): void {
    switch (e.code) {
      case 'Escape':
        this.closeSelectOnEsc(this.matSelect);
        break;
      case 'Tab':
        e.preventDefault();
        this.matSelect.focus();
    }
  }

  fetchObjectChooserValues(tips: string | string[]): void {
    this.contactSelfService
      .fetchObjectChooserValuesViaTips(tips, this.contactSelfServiceJWTToken)
      ?.pipe(take(1))
      .subscribe(
        (_data) => {
          const values = this.extractData(get(_data, 'values', []));
          this.isValueLoaded = true;
          if (values.length > 0) {
            if (this.to.isMultiSelect) {
              const finalValues = values?.map((elem) => elem?.value);
              this.selectedValue = finalValues;
              // sort items
              if (!isEmpty(this.sortingDirection)) {
                const sortedValues = orderBy(
                  values,
                  ['label'],
                  [this.sortingDirection === 'asc' ? 'asc' : 'desc']
                );
                this.originalObjectChooserValues = uniqBy(
                  [...sortedValues, ...this.to.objectChooserOptions],
                  'value'
                );
              } else {
                this.originalObjectChooserValues = uniqBy(
                  [...values, ...this.to.objectChooserOptions],
                  'value'
                );
              }
              this.displaySelectedItemsOnTop();

              this.formControl.setValue(finalValues);
              this.selectedChange.emit(finalValues);
            } else {
              const finalValue = get(values, '0.value');
              this.selectedValue = finalValue;
              this.originalObjectChooserValues = uniqBy(
                [...values, ...this.to.objectChooserOptions],
                'value'
              );
              this.displaySelectedItemsOnTop();

              this.formControl.setValue(finalValue);
              this.selectedChange.emit(finalValue);
            }
            this.formControl.markAsTouched();
            this.formControl.markAsDirty();
          }
        },
        (err) => {
          this.isValueLoaded = true;
        }
      );
  }

  extractData(data: string[]): any[] {
    const newData = [];
    data?.map((elem) => {
      const arr = elem?.split(':');
      const obj = {
        label: arr[1],
        value: arr[0],
      };
      newData.push(obj);
    });
    return newData;
  }

  onSelectedChange(event): void {
    this.updateSearchAndBackUpValues(event);
    this.isValueLoaded = true;
    this.formControl.setValue(event.value);
    this.selectedChange.emit(event.value);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
  }

  searchDebounce(): void {
    this.searchBar.valueChanges
      .pipe(
        filter(
          (queryVal) =>
            queryVal?.length >= this.minimumSearchLength ||
            queryVal?.length === 0
        ),
        debounceTime(2000),
        distinctUntilChanged(),
        switchMap((value) => {
          return value?.length > 0
            ? this.searchServiceCall(value, 0)
            : of(null);
        })
      )
      .subscribe((queriedValue) => {
        const queryLength = get(queriedValue, 'query', []).length;
        if (queryLength > 0) {
          this.whenSearchPerformedIsNotEmpty(queryLength, queriedValue);
        } else {
          if (this.searchBar.value?.length === 0) {
            this.whenSearchPerformedIsEmpty();
          }
        }

        this.cdr.detectChanges();
      });
  }

  onClosed(): void {
    this.searchBar.setValue('');
    this.offSetValue = 0;
    this.showMoreHide = false;
    this.handleKeyDownSub?.unsubscribe();

    // if dropdown is closed, modify the list to display selected items on the top

    if (
      !isEqual(sortBy(this.previousSelectedValue), sortBy(this.selectedValue))
    ) {
      this.displaySelectedItemsOnTop();
    }
    this.formControl.setValue(this.selectedValue);
    this.selectedChange.emit(this.selectedValue);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    this.backUpOfMultiSelectedValuesBeforeSearch = [];

    this.cdr.detectChanges();
  }

  showMoreObjects(): void {
    this.offSetValue = this.to.objectChooserOptions.length;
    this.searchServiceCall(this.searchBar.value, this.offSetValue).subscribe(
      (queriedValue) => {
        const queryLength = get(queriedValue, 'query', []).length;
        this.showMoreHide = queryLength < 100 ? true : false;
        if (queryLength > 0) {
          if (queryLength > 100) {
            queriedValue?.query.pop();
          }
          this.to.objectChooserOptions = uniqBy(
            [...this.to.objectChooserOptions, ...queriedValue?.query],
            'value'
          );
          this.originalObjectChooserValues = uniqBy(
            [...this.originalObjectChooserValues, ...queriedValue?.query],
            'value'
          );

          // * Since change detection is not happening automatically doing manual change detection
          this.cdr.detectChanges();
        }
      }
    );
  }

  searchServiceCall(searchKey: string, offset: number): Observable<any> {
    const queryChooserTip = this.to?.contextTypeTip;
    const filters = get(this.to, 'filter', null);
    const searchObjectParams = searchKey;
    return this.searchService.getSearchedObject(
      queryChooserTip,
      searchObjectParams,
      filters,
      offset,
      this.contactSelfServiceJWTToken ?? null
    );
  }

  ngAfterContentChecked(): void {
    this.checkShowMore();
  }

  // wrapper method written only for spec files
  checkShowMore() {
    if (this.to.objectChooserOptions.length < 100) {
      this.showMoreHide = true;
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.handleKeyDownSub?.unsubscribe();
  }

  closeSelectOnEsc(select: MatSelect): void {
    select.close();
  }

  matSelectHandleKeyDown(): void {
    if (!this.oldMatSelectKeydown) {
      this.oldMatSelectKeydown = this.matSelect._handleKeydown.bind(
        this.matSelect
      );
    }

    this.matSelect._handleKeydown = (event) => {
      if (event.code === 'Tab' && event.shiftKey && this.matSelect.panelOpen) {
        event.preventDefault();
        this.fromFieldInput.focus();
      } else {
        this.oldMatSelectKeydown(event);
      }
    };
  }

  displaySelectedItemsOnTop(): void {
    if (this.to.isMultiSelect) {
      this.updateSelectedValue();
    }
    let _selectedValue = cloneDeep(this.selectedValue);
    this.previousSelectedValue = cloneDeep(this.selectedValue);
    let objectChooserOptions = cloneDeep(this.originalObjectChooserValues);
    if (!isArray(_selectedValue)) {
      _selectedValue = [_selectedValue];
    }
    if (get(_selectedValue, 'length', 0) > 0) {
      this.getSelectedItemsFromObjectChooserOptions(
        objectChooserOptions,
        _selectedValue
      );
    } else {
      this.to.objectChooserOptions = cloneDeep(objectChooserOptions);
    }
  }

  getSelectedItemsFromObjectChooserOptions(
    objectChooserOptions: Array<any>,
    _selectedValue: string[]
  ): void {
    this.showMoreHide = objectChooserOptions?.length < 100 ? true : false;
    if (objectChooserOptions?.length > 100) {
      objectChooserOptions?.pop();
    }
    const itemsOnTop = [];
    _selectedValue.forEach((value) => {
      const option = objectChooserOptions?.find(
        (option) => option?.value === value
      );
      if (!isEmpty(option)) {
        itemsOnTop.push(option);
      }
    });

    // if condition is satisfied, push the selected values on top of object chooser options
    if (
      itemsOnTop?.length > 0 ||
      this.searchedSelectedObjectChooserValues?.length > 0
    ) {
      const options = [
        ...itemsOnTop,
        ...this.searchedSelectedObjectChooserValues,
      ];
      // sort items
      if (!isEmpty(this.sortingDirection)) {
        const sortedItemsOnTop = orderBy(
          options,
          ['label'],
          [this.sortingDirection === 'asc' ? 'asc' : 'desc']
        );

        this.to.objectChooserOptions = uniqBy(
          [...sortedItemsOnTop, ...objectChooserOptions],
          'value'
        );
      } else {
        this.to.objectChooserOptions = uniqBy(
          [...options, ...objectChooserOptions],
          'value'
        );
      }
      if (this.to.isMultiSelect) {
        this.backUpOfMultiSelectedOptionsBeforeSearch = cloneDeep(
          this.to.objectChooserOptions
        );
      }
    }
  }

  updateSelectedValue(): void {
    if (this.to.isMultiSelect) {
      this.selectedValue = uniq([
        ...this.backUpOfMultiSelectedValuesBeforeSearch,
        ...this.searchedSelectedObjectChooserValues?.map(
          (option) => option?.value
        ),
        ...this.selectedValue,
      ]);
    }
  }

  /**
   *
   * @param event contains details of selected and unselected values
   * - push the selected value into searchedSelectedObjectChooserValues
   * - remove the unselected value from searchedSelectedObjectChooserValues and backUpOfMultiSelectedValuesBeforeSearch
   */
  updateSearchAndBackUpValues(event): void {
    let selectedValue = cloneDeep(event?.value);
    if (!isArray(selectedValue)) {
      selectedValue = [selectedValue];
    }
    const _searchedSelectedObjectChooserValues = [];
    selectedValue.forEach((value) => {
      const option = this.searchedObjectChooserOptions?.find(
        (option) => option?.value === value
      );
      if (!isEmpty(option)) {
        _searchedSelectedObjectChooserValues.push(option);
      }
    });
    if (_searchedSelectedObjectChooserValues?.length > 0) {
      if (this.to.isMultiSelect) {
        this.searchedSelectedObjectChooserValues = uniqBy(
          [
            ...this.searchedSelectedObjectChooserValues,
            ..._searchedSelectedObjectChooserValues,
          ],
          'value'
        );
      } else {
        this.searchedSelectedObjectChooserValues = cloneDeep(
          _searchedSelectedObjectChooserValues
        );
      }
    }

    if (this.to.isMultiSelect) {
      let unSelected = defaultTo(
        get(event, 'source.options._results'),
        []
      )?.filter((elem) => !elem?._selected);
      for (
        let i = 0;
        i < this.searchedSelectedObjectChooserValues?.length;
        i++
      ) {
        if (
          unSelected?.some(
            (elem) =>
              elem?.value === this.searchedSelectedObjectChooserValues[i]?.value
          )
        ) {
          this.searchedSelectedObjectChooserValues.splice(i, 1);
        }
      }

      for (
        let i = 0;
        i < this.backUpOfMultiSelectedValuesBeforeSearch?.length;
        i++
      ) {
        if (
          unSelected?.some(
            (elem) =>
              elem?.value === this.backUpOfMultiSelectedValuesBeforeSearch[i]
          )
        ) {
          this.backUpOfMultiSelectedValuesBeforeSearch.splice(i, 1);
        }
      }
    }
  }

  whenSearchPerformedIsNotEmpty(queryLength: number, queriedValue): void {
    this.showMoreHide = queryLength < 100 ? true : false;
    if (queryLength > 100) {
      queriedValue?.query.pop();
    }
    if (this.to.isMultiSelect) {
      this.backUpOfMultiSelectedValuesBeforeSearch = uniq([
        ...this.backUpOfMultiSelectedValuesBeforeSearch,
        ...this.selectedValue,
      ]);
    }
    const itemsOnTop = [];
    const _selectedValue = isArray(this.selectedValue)
      ? this.selectedValue
      : [this.selectedValue];
    _selectedValue.forEach((value) => {
      const option = queriedValue?.query?.find(
        (option) => option?.value === value
      );
      if (!isEmpty(option)) {
        itemsOnTop.push(option);
      }
    });
    // sort items
    if (!isEmpty(this.sortingDirection)) {
      const sortedItemsOnTop = orderBy(
        itemsOnTop,
        ['label'],
        [this.sortingDirection === 'asc' ? 'asc' : 'desc']
      );
      this.to.objectChooserOptions = uniqBy(
        [...sortedItemsOnTop, ...queriedValue?.query],
        'value'
      );
    } else {
      this.to.objectChooserOptions = uniqBy(
        [...itemsOnTop, ...queriedValue?.query],
        'value'
      );
    }
    this.searchedObjectChooserOptions = queriedValue?.query;
    this.updateSelectedValue();
  }

  whenSearchPerformedIsEmpty(): void {
    if (this.to.isMultiSelect) {
      this.to.objectChooserOptions = cloneDeep(
        this.backUpOfMultiSelectedOptionsBeforeSearch?.length > 0
          ? this.backUpOfMultiSelectedOptionsBeforeSearch
          : this.originalObjectChooserValues
      );
      this.showMoreHide =
        this.to.objectChooserOptions?.length < 100 ? true : false;
      if (this.to.objectChooserOptions?.length > 100) {
        this.to.objectChooserOptions?.pop();
      }
      this.updateSelectedValue();
    } else {
      this.displaySelectedItemsOnTop();
    }
  }

  getPathName(): any {
    return get(window, 'location.pathname', null);
  }
}
